import React from 'react';
import { FocusContext, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import Dialog from '@smart-tv/components/dialog';
import DialogContent from '@smart-tv/components/dialog/dialog-content';
import testIds from '@smart-tv/e2e/shared/variables';
import styles from './npvr.module.scss';
import Typography from '@smart-tv/components/typography';
import Button from '@smart-tv/components/button/button';
import NpvrStorage from './npvr-storage';
import NpvrRecordings from './npvr-recordings';
import NpvrExpiring from './npvr-expiring';
import NpvrPlanned from './npvr-planned';
import NpvrErrors from './npvr-errors';
import { usePlayerContext } from '../player/_contexts/player-context';
import NotificationDot from '@smart-tv/components/notification-dot';
import useNpvrNotifications from '@smart-tv/hooks/use-npvr-notifications';
import RecordingDialog, {
  RecordingDialogContentProps
} from '@smart-tv/components/dialog/recording-dialog';
import { getNPVRTabFocusKey, NpvrTab, NpvrTabProps } from './npvr-helpers';

interface Props {
  open: boolean;
  onClose: () => void;
  initialTab?: NpvrTab;
}

interface NpvrBodyProps extends NpvrTabProps {
  initialTab: NpvrTab;
}

const tabMapper: Record<NpvrTab, React.ComponentType<NpvrTabProps>> = {
  [NpvrTab.Storage]: NpvrStorage,
  [NpvrTab.Recordings]: NpvrRecordings,
  [NpvrTab.Expiring]: NpvrExpiring,
  [NpvrTab.Planned]: NpvrPlanned,
  [NpvrTab.Errors]: NpvrErrors
};

const tabLabels: Record<NpvrTab, string> = {
  [NpvrTab.Storage]: 'Lagerplads',
  [NpvrTab.Recordings]: 'Optaget',
  [NpvrTab.Expiring]: 'Udløber',
  [NpvrTab.Planned]: 'Planlagt',
  [NpvrTab.Errors]: 'Fejl'
};

const tabKeys = Object.keys(tabMapper) as NpvrTab[];

// Rendered only while the Dialog is open, so the focus/escape hooks below mount fresh each time.
const NpvrBody: React.FC<NpvrBodyProps> = ({ setDialogProps, initialTab }) => {
  const [tab, setTab] = React.useState<NpvrTab>(initialTab);
  // Updated immediately on focus (unlike `tab`, which is debounced below) so the selected
  // tab highlight tracks focus instantly instead of lagging behind the content switch.
  const [selectedTab, setSelectedTab] = React.useState<NpvrTab>(initialTab);
  const { ref, focusKey, focusSelf } = useFocusable({
    preferredChildFocusKey: getNPVRTabFocusKey(initialTab),
    isFocusBoundary: true,
    focusBoundaryDirections: ['up']
  });
  const navigationTimerRef = React.useRef<number>();
  const { refresh, hasTabNotification, markTabSeen } = useNpvrNotifications();

  React.useEffect(() => {
    focusSelf();
  }, [focusSelf]);

  React.useEffect(() => {
    if (!hasTabNotification(tab)) return;

    (async () => {
      await markTabSeen(tab);
      await refresh();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  React.useEffect(
    () => () => {
      if (navigationTimerRef.current !== undefined) {
        window.clearTimeout(navigationTimerRef.current);
      }
    },
    []
  );

  const handleTabFocus = React.useCallback((tabKey: NpvrTab) => {
    setSelectedTab(tabKey);

    if (navigationTimerRef.current !== undefined) {
      window.clearTimeout(navigationTimerRef.current);
    }

    navigationTimerRef.current = window.setTimeout(() => {
      setTab(tabKey);
    }, 300);
  }, []);

  const handleTabSelect = React.useCallback((tabKey: NpvrTab) => {
    if (navigationTimerRef.current !== undefined) {
      window.clearTimeout(navigationTimerRef.current);
    }

    setSelectedTab(tabKey);
    setTab(tabKey);
  }, []);

  const NpvrTabContent = tabMapper[tab];

  return (
    <div className={styles['npvr']}>
      <Typography className={styles['npvr__title']} variant="h3">
        Optagelser
      </Typography>
      <FocusContext.Provider value={focusKey}>
        <nav className={styles['npvr__nav']} ref={ref} role="tablist">
          {tabKeys.map((tabKey) => (
            <Button
              key={tabKey}
              focusKey={getNPVRTabFocusKey(tabKey)}
              className={styles['npvr__nav-button']}
              variant="text"
              selected={selectedTab === tabKey}
              data-testid={testIds.npvr.tab[tabKey]}
              id={getNPVRTabFocusKey(tabKey)}
              role="tab"
              aria-selected={tab === tabKey}
              aria-controls="npvr-tabpanel"
              onClick={() => handleTabSelect(tabKey)}
              onNavFocus={() => handleTabFocus(tabKey)}
            >
              {tabLabels[tabKey]}
              {hasTabNotification(tabKey) && (
                <NotificationDot className={styles['npvr__nav-button-dot']} />
              )}
            </Button>
          ))}
        </nav>
      </FocusContext.Provider>

      <div
        className={styles['npvr__content']}
        role="tabpanel"
        id="npvr-tabpanel"
        aria-labelledby={getNPVRTabFocusKey(tab)}
      >
        <NpvrTabContent setDialogProps={setDialogProps} />
      </div>
    </div>
  );
};

export const Npvr: React.FC<Props> = (props: Props) => {
  const { open, onClose, initialTab = NpvrTab.Storage } = props;
  const { player } = usePlayerContext();
  const [dialogProps, setDialogProps] = React.useState<RecordingDialogContentProps | null>(null);

  if (!open) return null;

  const npvrHiddenByPlayer = !!player;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableEscapeKeyDown={npvrHiddenByPlayer || dialogProps !== null}
    >
      <DialogContent>
        <NpvrBody setDialogProps={setDialogProps} initialTab={initialTab} />
        <RecordingDialog dialogProps={dialogProps} onClose={() => setDialogProps(null)} />
      </DialogContent>
    </Dialog>
  );
};

export default Npvr;
